import React, { useEffect, useRef, useState } from 'react';
import {
  addWebSocketListener,
  getWebSocketStatus,
  removeWebSocketListener,
  sendWebSocketMessage,
} from '../../services/websocketSingleton';
import { apiFetch } from '../../apiFetch';
import './MiniPlayer.css';

const RADIO_PLAYER_DEEP_LINK = 'radio-player://play';
const DEEP_LINK_FALLBACK_MS = 2500;

const BROWSER_WS_MESSAGES = {
  AUTH_MISSING_LOCAL: 'Sessão do painel ausente. Entre novamente.',
  JWT_INVALID: 'Sessão do painel expirada. Entre novamente.',
  SESSION_INVALID: 'Sessão do painel expirada. Entre novamente.',
  CLIENTE_ID_MISMATCH: 'Sessão do painel pertence a outro cliente. Entre novamente.',
  ORIGIN_NOT_ALLOWED: 'Origem do painel não autorizada para WebSocket.',
  REGISTER_REJECTED: 'Painel não autenticado no WebSocket.',
  max_attempts: 'WebSocket do painel não conectou após várias tentativas.',
  time_window: 'WebSocket do painel ficou muito tempo tentando conectar.',
  connection_error: 'Erro ao conectar o WebSocket do painel.',
};

function formatarTempo(segundos) {
  if (!segundos || isNaN(segundos)) return '0:00';
  const min = Math.floor(segundos / 60);
  const seg = Math.floor(segundos % 60);
  return `${min}:${seg.toString().padStart(2, '0')}`;
}

function getBrowserWsMessage(reason) {
  return BROWSER_WS_MESSAGES[reason] || 'Painel sem canal WebSocket autenticado.';
}

function MiniPlayer() {
  const [status, setStatus] = useState({
    track: null, currentTime: 0, duration: 0, playing: false, volume: 1, connected: false, spotPlaying: false,
  });
  const [trackManagerOpen, setTrackManagerOpen] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [provisionCode, setProvisionCode] = useState(null);
  const [provisionDeepLink, setProvisionDeepLink] = useState(null);
  const [provisionError, setProvisionError] = useState(null);
  const [provisionCountdown, setProvisionCountdown] = useState(null);
  const [provisionExpiresAt, setProvisionExpiresAt] = useState(null);
  const [launchMessage, setLaunchMessage] = useState('');
  const [browserWs, setBrowserWs] = useState({
    connected: false,
    authenticated: false,
    registrationError: null,
    fatalReason: null,
  });
  const launchFallbackTimerRef = useRef(null);
  const trackRefreshTimerRef = useRef(null);
  const lastSelectedTrackIndexRef = useRef(null);
  const { track, currentTime, duration, playing, volume, connected, spotPlaying } = status;
  const isSpotPlaying = !!spotPlaying;
  const musicPlaying = !!playing && !isSpotPlaying;
  const selectedTrackSet = new Set(selectedTracks);
  const provisionCountdownText = provisionCountdown || '...';
  const browserWsBlocked = !browserWs.authenticated && !!(browserWs.registrationError || browserWs.fatalReason);
  const browserWsReason = browserWs.registrationError || browserWs.fatalReason;
  const browserWsMessage = getBrowserWsMessage(browserWsReason);

  useEffect(() => {
    function syncBrowserWsStatus() {
      const wsStatus = getWebSocketStatus();
      setBrowserWs(prev => ({
        connected: !!wsStatus.connected,
        authenticated: !!wsStatus.authenticated,
        registrationError: wsStatus.registrationError || (wsStatus.authenticated ? null : prev.registrationError),
        fatalReason: wsStatus.connected ? null : prev.fatalReason,
      }));
    }

    function onMessage(event) {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'WS_OPEN') {
          setBrowserWs(prev => ({ ...prev, connected: true, fatalReason: null }));
        }
        if (msg.type === 'REGISTER_OK') {
          setBrowserWs({
            connected: true,
            authenticated: true,
            registrationError: null,
            fatalReason: null,
          });
        }
        if (msg.type === 'REGISTER_REJECTED') {
          setBrowserWs({
            connected: true,
            authenticated: false,
            registrationError: msg.reason || 'REGISTER_REJECTED',
            fatalReason: null,
          });
          setStatus(prev => ({ ...prev, connected: false }));
        }
        if (msg.type === 'WS_ERROR') {
          setBrowserWs(prev => ({
            ...prev,
            fatalReason: prev.authenticated ? prev.fatalReason : 'connection_error',
          }));
        }
        if (msg.type === 'WS_CLOSED') {
          setBrowserWs(prev => ({
            ...prev,
            connected: false,
            authenticated: false,
          }));
          setStatus(prev => ({ ...prev, connected: false }));
        }
        if (msg.type === 'WS_FATAL') {
          setBrowserWs(prev => ({
            ...prev,
            connected: false,
            authenticated: false,
            fatalReason: msg.reason || 'connection_error',
          }));
          setStatus(prev => ({ ...prev, connected: false }));
        }
        if (msg.type === 'MUSIC_STATUS') {
          setBrowserWs({
            connected: true,
            authenticated: true,
            registrationError: null,
            fatalReason: null,
          });
          setStatus({
            track:       msg.track       || null,
            currentTime: msg.currentTime || 0,
            duration:    msg.duration    || 0,
            playing:     !!msg.playing,
            volume:      msg.volume      ?? 1,
            connected:   !!msg.connected,
            spotPlaying: !!msg.spotPlaying,
          });
        }
        if (msg.type === 'TRACK_LIST') {
          const nextTracks = Array.isArray(msg.tracks) ? msg.tracks : [];
          setTracks(nextTracks);
          setSelectedTracks(prev => prev.filter(filename => nextTracks.includes(filename)));
          setTracksLoading(false);
        }
      } catch {}
    }
    addWebSocketListener(onMessage, 'MiniPlayer');
    syncBrowserWsStatus();
    const interval = setInterval(syncBrowserWsStatus, 3000);
    return () => {
      clearInterval(interval);
      removeWebSocketListener(onMessage, 'MiniPlayer');
    };
  }, []);

  useEffect(() => {
    if (!connected) return;

    setProvisionCode(null);
    setProvisionDeepLink(null);
    setProvisionError(null);
    setProvisionCountdown(null);
    setProvisionExpiresAt(null);
    setLaunchMessage('');
    if (launchFallbackTimerRef.current) {
      clearTimeout(launchFallbackTimerRef.current);
      launchFallbackTimerRef.current = null;
    }
  }, [connected]);

  useEffect(() => {
    if (!provisionExpiresAt) return undefined;

    function updateCountdown() {
      const expiresAtMs = new Date(provisionExpiresAt).getTime();
      if (!Number.isFinite(expiresAtMs)) {
        setProvisionCode(null);
        setProvisionDeepLink(null);
        setProvisionExpiresAt(null);
        setProvisionCountdown(null);
        return;
      }

      const remaining = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      if (remaining === 0) {
        setProvisionCode(null);
        setProvisionDeepLink(null);
        setProvisionExpiresAt(null);
        setProvisionCountdown(null);
        return;
      }

      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      setProvisionCountdown(`${m}:${String(s).padStart(2, '0')}`);
    }

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [provisionExpiresAt]);

  useEffect(() => {
    if (connected) return;
    setTrackManagerOpen(false);
    setTracks([]);
    setTracksLoading(false);
    setSelectedTracks([]);
    lastSelectedTrackIndexRef.current = null;
  }, [connected]);

  useEffect(() => () => {
    if (launchFallbackTimerRef.current) clearTimeout(launchFallbackTimerRef.current);
    if (trackRefreshTimerRef.current) clearTimeout(trackRefreshTimerRef.current);
  }, []);

  function scheduleDeepLinkFeedback() {
    setLaunchMessage('Abrindo Radio Player...');

    if (launchFallbackTimerRef.current) {
      clearTimeout(launchFallbackTimerRef.current);
    }

    launchFallbackTimerRef.current = setTimeout(() => {
      setLaunchMessage('Se o player não abrir, inicie o Radio Player manualmente.');
      launchFallbackTimerRef.current = null;
    }, DEEP_LINK_FALLBACK_MS);
  }

  function abrirRadioPlayer(e) {
    e.stopPropagation();
    scheduleDeepLinkFeedback();
    window.location.href = RADIO_PLAYER_DEEP_LINK;
  }

  function abrirProvisionDeepLink(e) {
    e.stopPropagation();
    scheduleDeepLinkFeedback();
  }

  function relogarPainel(e) {
    e.stopPropagation();
    localStorage.removeItem('radio-ai-jwt');
    window.location.href = '/login';
  }

  function recarregarPainel(e) {
    e.stopPropagation();
    window.location.reload();
  }

  async function gerarProvision(e) {
    e?.stopPropagation();
    setProvisionLoading(true);
    setProvisionError(null);

    try {
      const res = await apiFetch('/api/auth/player-provision', {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Erro ao gerar provisão');

      const data = await res.json();
      setProvisionCode(data.code);
      setProvisionDeepLink(data.deepLink || null);
      setProvisionExpiresAt(data.expiresAt);
      setProvisionCountdown(null);
    } catch {
      setProvisionError('Não foi possível gerar o código. Tente novamente.');
    } finally {
      setProvisionLoading(false);
    }
  }

  function requestTrackList() {
    setTracksLoading(true);
    const sent = sendWebSocketMessage('PLAYER_LIST_TRACKS');
    if (!sent) setTracksLoading(false);
  }

  function scheduleTrackRefresh() {
    setTracksLoading(true);
    if (trackRefreshTimerRef.current) clearTimeout(trackRefreshTimerRef.current);
    trackRefreshTimerRef.current = setTimeout(() => {
      requestTrackList();
      trackRefreshTimerRef.current = null;
    }, 300);
  }

  function toggleTrackManager(e) {
    e.stopPropagation();
    const nextOpen = !trackManagerOpen;
    setTrackManagerOpen(nextOpen);
    if (nextOpen) requestTrackList();
  }

  function removeTrack(e, filename) {
    e.stopPropagation();
    const confirmed = window.confirm(`Excluir "${filename}" da pasta de músicas do Radio Player?`);
    if (!confirmed) return;

    const sent = sendWebSocketMessage('PLAYER_REMOVE_TRACK', { filename });
    if (!sent) return;

    setTracks(prev => prev.filter(item => item !== filename));
    setSelectedTracks(prev => prev.filter(item => item !== filename));
    scheduleTrackRefresh();
  }

  function toggleTrackSelection(e, filename, index) {
    e.stopPropagation();
    const checked = e.currentTarget.checked;
    const lastIndex = lastSelectedTrackIndexRef.current;

    setSelectedTracks(prev => {
      const next = new Set(prev);

      if (e.shiftKey && lastIndex !== null) {
        const start = Math.min(lastIndex, index);
        const end = Math.max(lastIndex, index);
        tracks.slice(start, end + 1).forEach(item => {
          if (checked) next.add(item);
          else next.delete(item);
        });
      } else if (checked) {
        next.add(filename);
      } else {
        next.delete(filename);
      }

      return tracks.filter(item => next.has(item));
    });

    lastSelectedTrackIndexRef.current = index;
  }

  function clearTrackSelection(e) {
    e.stopPropagation();
    setSelectedTracks([]);
    lastSelectedTrackIndexRef.current = null;
  }

  function removeSelectedTracks(e) {
    e.stopPropagation();
    const total = selectedTracks.length;
    if (!total) return;

    const confirmed = window.confirm(`Excluir ${total} música${total > 1 ? 's' : ''} da pasta do Radio Player?`);
    if (!confirmed) return;

    let sentCount = 0;
    selectedTracks.forEach(filename => {
      if (sendWebSocketMessage('PLAYER_REMOVE_TRACK', { filename })) sentCount += 1;
    });

    if (!sentCount) return;

    const sentSet = new Set(selectedTracks);
    setTracks(prev => prev.filter(item => !sentSet.has(item)));
    setSelectedTracks([]);
    lastSelectedTrackIndexRef.current = null;
    scheduleTrackRefresh();
  }

  function renderTrackManager() {
    if (!trackManagerOpen) return null;

    return (
      <div className="mini-track-manager" onClick={(e) => e.stopPropagation()}>
        <div className="mini-track-manager-header">
          <div>
            <strong>Músicas</strong>
            {selectedTracks.length > 0 && (
              <span className="mini-track-selected-count">
                {selectedTracks.length} selecionada{selectedTracks.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="mini-track-actions">
            {selectedTracks.length > 0 && (
              <>
                <button
                  className="mini-track-clear"
                  onClick={clearTrackSelection}
                  title="Limpar seleção"
                >
                  Limpar
                </button>
                <button
                  className="mini-track-delete-selected"
                  onClick={removeSelectedTracks}
                  title="Excluir músicas selecionadas"
                >
                  Excluir ({selectedTracks.length})
                </button>
              </>
            )}
            <button
              className="mini-track-refresh"
              onClick={requestTrackList}
              title="Atualizar lista"
            >
              Atualizar
            </button>
          </div>
        </div>

        <div className="mini-track-list">
          {tracksLoading && tracks.length === 0 ? (
            <div className="mini-track-empty">Carregando...</div>
          ) : tracks.length === 0 ? (
            <div className="mini-track-empty">Nenhuma música na pasta</div>
          ) : (
            tracks.map((filename, index) => (
              <div key={filename} className={`mini-track-item ${selectedTrackSet.has(filename) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  className="mini-track-checkbox"
                  checked={selectedTrackSet.has(filename)}
                  onClick={(e) => toggleTrackSelection(e, filename, index)}
                  readOnly
                  title="Selecionar música"
                />
                <span title={filename}>{filename}</span>
                <button
                  className="mini-track-remove"
                  onClick={(e) => removeTrack(e, filename)}
                  title="Excluir música"
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  if (browserWsBlocked || !connected) return (
    <div className={`mini-player ${isSpotPlaying ? 'mini-player-spot' : ''}`}>
      <div className="mini-player-content">
        <div className="mini-info">
          <div className="mini-titulo">
            {browserWsBlocked
              ? 'Painel sem canal seguro'
              : provisionCode ? 'Aguardando Radio Player...' : 'Sistema de som desligado'}
          </div>
          <div className={`mini-status ${browserWsBlocked ? 'mini-status-error' : ''}`}>
            {browserWsBlocked
              ? browserWsMessage
              : provisionCode
              ? `Código: ${provisionCode} · Expira em ${provisionCountdownText}`
              : launchMessage || 'Radio Player não conectado'}
          </div>
        </div>
        <div className="mini-controles">
          {browserWsBlocked ? (
            <button
              className="mini-btn mini-btn-provision"
              onClick={browserWs.registrationError ? relogarPainel : recarregarPainel}
              title={browserWs.registrationError ? 'Entrar novamente' : 'Recarregar painel'}
            >
              {browserWs.registrationError ? 'Entrar' : 'Recarregar'}
            </button>
          ) : provisionCode && provisionDeepLink ? (
            <a
              className="mini-btn mini-btn-provision"
              href={provisionDeepLink}
              onClick={abrirProvisionDeepLink}
              title="Conectar Radio Player"
            >
              Conectar
            </a>
          ) : (
            <button
              className="mini-btn mini-btn-provision"
              onClick={gerarProvision}
              disabled={provisionLoading}
              title="Provisionar Radio Player"
            >
              {provisionLoading ? '...' : 'Provisionar'}
            </button>
          )}
          {!browserWsBlocked && (
            <button
              className="mini-btn mini-btn-play"
              onClick={abrirRadioPlayer}
              title="Abrir Radio Player"
            >
              ▶
            </button>
          )}
        </div>
      </div>
      {provisionCode && (
        <div className="mini-provision-hint">
          {launchMessage || 'Abra o Radio Player, clique em Conectar ou insira o código manual no exe'}
        </div>
      )}
      {provisionError && (
        <div className="mini-provision-error">{provisionError}</div>
      )}
    </div>
  );

  if (!track) return (
    <div className="mini-player">
      <div className="mini-player-content">
        <div className="mini-info">
          <div className="mini-titulo">Conectado - sem músicas</div>
          <div className="mini-status">Adicione músicas para começar</div>
        </div>
        <div className="mini-controles">
          <button
            className="mini-btn"
            onClick={(e) => { e.stopPropagation(); sendWebSocketMessage('PLAYER_ADD_MUSIC'); }}
            title="Adicionar Música"
          >
            +
          </button>
          <button
            className="mini-btn"
            onClick={toggleTrackManager}
            title="Gerenciar Músicas"
          >
            ☰
          </button>
        </div>
      </div>
      {renderTrackManager()}
    </div>
  );

  return (
    <div className="mini-player">
      <div className="mini-player-content">
        {/* Informações da Música */}
        <div className="mini-info">
          <div className="mini-titulo" title={track}>
            {track}
          </div>
          <div className="mini-status">
            {isSpotPlaying ? 'Spot comercial - músicas pausadas' : musicPlaying ? 'Tocando' : 'Pausado'} - Volume: {Math.round(volume * 100)}%
          </div>
          <div className="mini-tempo">
            {formatarTempo(currentTime)} / {formatarTempo(duration)}
          </div>
        </div>

        {/* Controles */}
        <div className="mini-controles">
          <button
            onClick={(e) => { e.stopPropagation(); sendWebSocketMessage('PLAYER_PREV'); }}
            className="mini-btn"
            disabled={isSpotPlaying}
            title={isSpotPlaying ? 'Bloqueado durante spot comercial' : 'Música Anterior'}
          >
            ‹
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isSpotPlaying) return;
              sendWebSocketMessage(musicPlaying ? 'PLAYER_PAUSE' : 'PLAYER_RESUME');
            }}
            className="mini-btn mini-btn-play"
            disabled={isSpotPlaying}
            title={isSpotPlaying ? 'Spot comercial em execução' : musicPlaying ? 'Pausar' : 'Tocar'}
          >
            {isSpotPlaying ? 'II' : musicPlaying ? 'II' : '▶'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); sendWebSocketMessage('PLAYER_NEXT'); }}
            className="mini-btn"
            disabled={isSpotPlaying}
            title={isSpotPlaying ? 'Bloqueado durante spot comercial' : 'Próxima Música'}
          >
            ›
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); sendWebSocketMessage('PLAYER_ADD_MUSIC'); }}
            className="mini-btn"
            title="Adicionar Música"
          >
            +
          </button>
          <button
            onClick={toggleTrackManager}
            className="mini-btn"
            title="Gerenciar Músicas"
          >
            ☰
          </button>
        </div>

        {/* Barra de Progresso */}
        <div className="mini-progress-container">
          <div
            className="mini-progress-bar"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
      </div>
      {renderTrackManager()}
    </div>
  );
}

export default MiniPlayer;
